import { Location } from '@angular/common';
import { UtilService } from 'src/app/core/services/util/util.service';
import { ReplaySubject, Subscription } from 'rxjs';
import { Injectable, OnDestroy, Injector, inject, signal } from '@angular/core';
import { NavigationEnd, Router, Event } from '@angular/router';
import { REQUEST, RESPONSE } from '../../../../express.tokens';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { I18nService } from '../i18n/i18n.service';
import { AppGlobalsModule } from 'src/app/app-globals.module';
import { BrowserPlatformService } from '../browser-platform/browser-platform.service';

@Injectable({
    providedIn: 'root',
})
export class UrlAddressService implements OnDestroy {
    private _request = inject(REQUEST, { optional: true })!;
    private _response = inject(RESPONSE, { optional: true });
    private _router = inject(Router);
    private _i18n = inject(I18nService);
    private _utils = inject(UtilService);
    private _location = inject(Location);
    private _browser = inject(BrowserPlatformService);

    readonly contentAreaRoutes: string[] = ['midrash', 'ask'];
    readonly angularRoutes: string[] = [
        'midrash',
        'ask',
        'rabbi',
        'article',
        'users',
        'test',
        'search',
        'tags',
        'contact',
        'hofeshlimud',
        'nihul',
        'events/memorial', //the rest of /events is still MVC
        'calendar/timestable', //TODO: remove this when calendar will be angular
        'calendar/shabbattimes', //TODO: remove this when calendar will be angular
        'calendar/moladot', //TODO: remove this when calendar will be angular
        'calendar/barmitzva', //TODO: remove this when calendar will be angular
        'calendar/pesachtimes', //TODO: remove this when calendar will be angular
    ];
    readonly _pathsOutsideAngular: string[] = [
        '/midrash/closelessons',
        '/midrash/yberavs',
        '/ask/ask',
        'users/loginframe',
        '/calendar/timesday',
        '/calendar/shabbattimes',
        '/calendar/moladot',
        '/calendar/barmitzva',
        '/calendar/pesachtimes',
    ];
    readonly betEllRabbisPage = 'yberavs';
    private _subscriptions: Subscription[] = [];
    public isDedicationDatePicker: boolean;
    private _otherLangLinkOverride$ = new BehaviorSubject<string | undefined>(undefined);

    constructor() {
        this._isAngularApp$ = new BehaviorSubject<boolean>(this._getIsAngularApp());
        this.isDedicationDatePicker = this.getURL().includes('datepicker');
        this._isFilterPage$ = new BehaviorSubject<boolean>(false);
        this._isHomeContentArea$ = new BehaviorSubject<boolean>(false);
        this._activeLocation$ = new BehaviorSubject<SiteLocations>(this.getActiveLocation());
        this._activeAngularPage$ = new ReplaySubject<AngularPages>(1);
        this._subscriptions.push(
            this._router.events.pipe(filter((e: Event | NavigationEnd): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((ev: NavigationEnd) => {
                this._setActiveLocation(this.getActiveLocation());
                this._setIsContentAreaRootUrl(ev);
                this._setRootAngularUrl(ev.url);
            }),
        );
        this._subscriptions.push(
            this.getRootAngularUrl().subscribe((rootAngularUrl: string) => {
                this.rootAngularUrl = rootAngularUrl;
            }),
        );
        this._subscriptions.push(
            this.getActiveAngularPage().subscribe((activeAngularPage: AngularPages) => {
                // Some components uses it directly in HTML, thus the active page has unexpected behaviour
                // For that we need to enforce chance detection for the whole application
                // FIXME: this is an extremly bad practice, binding should have been inside compounent themseves
                setTimeout(() => {
                    {
                        this.activeAngularPage = activeAngularPage;
                    }
                }, 0);

                // And need another time directly for the uniersal, did I mention it's a bad practice?
                this.activeAngularPage = activeAngularPage;
            }),
        );
    }

    ngOnDestroy(): void {
        this._utils.unsubscribe(this._subscriptions);
        if (this._isFilterPage$) {
            this._isFilterPage$.unsubscribe();
        }
        if (this._isHomeContentArea$) {
            this._isHomeContentArea$.unsubscribe();
        }
        if (this._activeLocation$) {
            this._activeLocation$.unsubscribe();
        }
        if (this._activeAngularPage$) {
            this._activeAngularPage$.unsubscribe();
        }
        if (this._rootAngularUrl$) {
            this._rootAngularUrl$.unsubscribe();
        }
        if (this._isAngularApp$) {
            this._isAngularApp$.unsubscribe();
        }
        if (this._isContentArea$) {
            this._isContentArea$.unsubscribe();
        }
        if (this._otherLangLinkOverride$) {
            this._otherLangLinkOverride$.unsubscribe();
        }
    }

    public siteLocations = SiteLocations;

    /**
     * Replaces the current URL with a canonical route (e.g. redirecting a case-variant tag slug to the
     * canonical one). In the browser this is an in-app history replace. On the server it must NOT be a
     * router navigation: navigating mid-render re-enters the route and the error is caught by the global
     * ErrorsHandler as a 500 (see errors-handler.ts) even though the HTML still streams out - which fails
     * the staging deploy response tests. Instead we emit a real 301 to the canonical URL, which is also
     * the correct signal for crawlers. The target URL is built once via the router so encoding and query
     * serialization stay consistent across both platforms.
     *
     * @param commands Router link commands for the canonical route, e.g. ['/tags', canonicalSlug].
     * @param queryParams Query params to preserve on the canonical URL.
     * @param page Optional 1-based page; appended as a path segment when greater than 1.
     */
    public replaceWithCanonical(commands: (string | number)[], queryParams: Record<string, string> = {}, page?: number): void {
        const routeCommands = page && page > 1 ? [...commands, String(page)] : commands;
        const urlTree = this._router.createUrlTree(routeCommands, { queryParams });

        if (AppGlobalsModule.isServer) {
            this._response?.redirect(301, this._router.serializeUrl(urlTree));
            return;
        }

        void this._router.navigateByUrl(urlTree, { replaceUrl: true });
    }

    private _activeLocation$: BehaviorSubject<SiteLocations>;
    private _setActiveLocation(activeLocation: SiteLocations): void {
        this._activeLocation$.next(activeLocation);
    }
    public getActiveLocation$(): Observable<SiteLocations> {
        return this._activeLocation$.asObservable();
    }

    private _firstLoadPage$ = new BehaviorSubject<boolean>(true);
    public setFirstLoadPage(isFirstPage: boolean): void {
        this._firstLoadPage$.next(isFirstPage);
    }
    /**
     * Indicate if we're on the first time & url the aplication is opened at
     *
     * @returns First page state Observable
     */
    public getFirstLoadPage$(): Observable<boolean> {
        return this._firstLoadPage$.asObservable();
    }

    // Need to be set in on init of individiual components.
    // Signal-backed so OnPush templates reading it directly are tracked; the property syntax stays unchanged.
    private readonly _activeAngularPage = signal<AngularPages | undefined>(undefined);
    public get activeAngularPage(): AngularPages | undefined {
        return this._activeAngularPage();
    }
    public set activeAngularPage(activeAngularPage: AngularPages | undefined) {
        this._activeAngularPage.set(activeAngularPage);
    }
    private _activeAngularPage$: ReplaySubject<AngularPages>;
    public setActiveAngularPage(activeAngularPage: AngularPages): void {
        this._activeAngularPage$.next(activeAngularPage);
    }
    public getActiveAngularPage(): Observable<AngularPages> {
        return this._activeAngularPage$.asObservable();
    }

    public getActiveLocation(): SiteLocations {
        // Exract the active location form the first elemnt from the URL
        let currentlocation = this._getCurrentlocation();
        if (currentlocation.startsWith(this.siteLocations.Ask)) {
            return this.siteLocations.Ask;
        } else if (currentlocation.startsWith(this.siteLocations.Midrash)) {
            return this.siteLocations.Midrash;
        } else if (currentlocation.startsWith(this.siteLocations.Calendar)) {
            return this.siteLocations.Calendar;
        } else if (currentlocation.startsWith(this.siteLocations.Wiki)) {
            return this.siteLocations.Wiki;
        } else if (currentlocation.startsWith(this.siteLocations.Article)) {
            return this.siteLocations.Article;
        } else if (currentlocation.startsWith(this.siteLocations.Users)) {
            return this.siteLocations.Users;
        } else if (currentlocation.startsWith(this.siteLocations.Exams)) {
            return this.siteLocations.Exams;
        } else if (currentlocation.startsWith(this.siteLocations.Tags)) {
            return this.siteLocations.Tags;
        } else if (currentlocation === '' || currentlocation.startsWith('?') || currentlocation.startsWith('#')) {
            // Main page can be empty or can have parameters
            return this.siteLocations.Main;
        } else {
            return this.siteLocations.Other;
        }
    }

    private _getCurrentlocation(): string {
        let url = this.getURL();
        let baseUrl = url.split('/')[url.startsWith('http') ? 3 : 1] ?? '';
        //TODO: remove if when calendar will be angular
        if (baseUrl.startsWith('calendar')) {
            return baseUrl + '/' + (url.split('/')[url.startsWith('http') ? 4 : 2] ?? '');
        }
        return baseUrl;
    }

    public getURL(): string {
        //server
        if (this._request && this._request.url) {
            return this._request.url;
        }

        const href = this._browser.location?.href;
        if (href) {
            return href;
        }

        return '';
    }

    /**
     * The current PATH (leading slash, no origin/query) on both platforms - getURL() returns
     * the server request path but the full location.href on the client, so path checks
     * against it silently fail in the browser (the /nihul chrome gating was the casualty).
     */
    public getPathname(): string {
        const url = this.getURL();
        if (!url) {
            return '/';
        }
        try {
            return new URL(url, 'http://internal').pathname;
        } catch {
            return '/';
        }
    }

    /**
     * Reactive stream of the opposite-language link.
     * Combines the active Angular page with NavigationEnd events so the link
     * updates both when a component sets its page identity and when the route changes.
     * Consumers should use toSignal() to bind the result.
     */
    public getOtherLangLink$(): Observable<string> {
        const navigationEnd$ = this._router.events.pipe(
            filter((e: Event | NavigationEnd): e is NavigationEnd => e instanceof NavigationEnd),
            startWith(undefined),
        );
        return combineLatest([this._activeAngularPage$.pipe(startWith(undefined as AngularPages | undefined)), navigationEnd$, this._otherLangLinkOverride$]).pipe(
            map(([page, , override]) => override || this._buildOtherLangLink(page)),
        );
    }

    public setOtherLangLinkOverride(link: string): void {
        this._otherLangLinkOverride$.next(link);
    }

    public clearOtherLangLinkOverride(): void {
        this._otherLangLinkOverride$.next(undefined);
    }

    /** Builds the opposite-language URL based on the current active page context. */
    private _buildOtherLangLink(activeAngularPage: AngularPages | undefined): string {
        if (activeAngularPage === AngularPages.Rabbi) {
            return this._i18n.getOpositeLangBaseUrl() + '/' + this.siteLocations.Midrash;
        } else if (activeAngularPage === AngularPages.Content) {
            return this._i18n.getOpositeLangBaseUrl() + '/' + this.getActiveLocation();
        } else if (activeAngularPage === AngularPages.ArticleDetails) {
            return this._i18n.getOpositeLangBaseUrl() + '/' + this.siteLocations.Article;
        } else if (this.getURL().includes(this.betEllRabbisPage)) {
            return this._i18n.getOpositeLangBaseUrl();
        }
        return this._i18n.getOpositeLangBaseUrl() + this._getPathname();
    }

    public ifAngularPage(): boolean {
        const currentUrl = this.getURL();
        const currentlocation = this._getCurrentlocation().split('?')[0].split('#')[0];

        if (this._pathsOutsideAngular.some((pathOut) => currentUrl.includes(pathOut))) {
            return false;
        } else if (currentlocation === '') {
            return true;
        }

        return this.angularRoutes.some((angularRoute) => currentlocation === angularRoute || currentlocation.startsWith(`${angularRoute}/`));
    }

    /** Need boolean because sophisticated reaons */
    public getParamClient(val: string, event: any, returnFalseOnServer: boolean = false): boolean {
        if (!AppGlobalsModule.isServer) {
            if (event?.url?.indexOf(val) > -1) {
                return true;
            } else if (returnFalseOnServer) {
                return false;
            } else {
                return false;
            }
        } else {
            throw new Error('This method is only for client side');
        }
    }

    public getParam(val: string) {
        // Server:
        if (this._request) {
            return this._request.query[val] ? this._request.query[val] : '';
        }
        return new URLSearchParams(this._getSearch()).get(val) ?? undefined;
    }

    // Signal-backed so OnPush templates reading it directly are tracked; the property syntax stays unchanged.
    private readonly _rootAngularUrl = signal<string>('');
    public get rootAngularUrl(): string {
        return this._rootAngularUrl();
    }
    public set rootAngularUrl(rootAngularUrl: string) {
        this._rootAngularUrl.set(rootAngularUrl);
    }
    private _rootAngularUrl$: Subject<string> = new Subject<string>();
    public setRootAngualrUrl(rootAngularUrl: string): void {
        this._rootAngularUrl$.next(rootAngularUrl);
    }
    public getRootAngularUrl(): Observable<string> {
        return this._rootAngularUrl$.asObservable();
    }

    private _isFilterPage$: BehaviorSubject<boolean>;
    public getIsFilterPage(): Observable<boolean> {
        return this._isFilterPage$.asObservable();
    }
    public setIsFilterPage(isFilterPage: boolean): void {
        this._isFilterPage$.next(isFilterPage);
    }

    /**
     * Indicates whether current url is inside main angular application
     *
     * @returns Current url state observable
     */
    private _isAngularApp$: BehaviorSubject<boolean>;
    public getIsAngularApp(): Observable<boolean> {
        return this._isAngularApp$.asObservable();
    }
    public setIsAngularApp(isAngularApp: boolean): void {
        this._isAngularApp$.next(isAngularApp);
    }

    private _getIsAngularApp(): boolean {
        let currentlocation = this._getCurrentlocation();

        // Stript queries if they're exist since they're irrelevant
        currentlocation = currentlocation.split('?')[0];

        return (
            currentlocation === '' ||
            (this.angularRoutes.some((angularRoute) => currentlocation.includes(angularRoute)) &&
                this._pathsOutsideAngular.every((_) => !currentlocation.includes(_)))
        );
    }

    private _isContentArea$: Subject<boolean> = new Subject<boolean>();
    public getIsContentArea(): Observable<boolean> {
        return this._isContentArea$.asObservable();
    }
    public setIsContentArea(isContentArea: boolean): void {
        this._isContentArea$.next(isContentArea);
    }

    private _isHomeContentArea$: BehaviorSubject<boolean>;
    public getIsHomeContentArea(): Observable<boolean> {
        return this._isHomeContentArea$.asObservable();
    }
    public setIsHomeContentArea(isHomeContentArea: boolean): void {
        this._isHomeContentArea$.next(isHomeContentArea);
    }

    private _getUrlPathSlugs(url: string): string[] {
        return url.split('/').filter((urlPathSlug) => !!urlPathSlug);
    }

    private _getPathname(): string {
        if (this._request?.url) {
            return this._request.url.split('?')[0].split('#')[0];
        }
        return this._browser.location?.pathname ?? '';
    }

    private _getSearch(): string {
        if (this._request?.url) {
            const query = this._request.url.split('?')[1]?.split('#')[0];
            return query ? '?' + query : '';
        }
        return this._browser.location?.search ?? '';
    }

    public getUrlfragment(): string {
        return this.getURL().split('#')[1];
    }

    public setUrlNoUpdate(newUrl: string): void {
        this._location.go(newUrl);
    }

    private _setIsContentAreaRootUrl(ev: NavigationEnd) {
        let _getIsContentAreaSubscription = this.getIsContentArea().subscribe((isContentArea: boolean) => {
            let _isContentAreaRootUrl = isContentArea && this._getIsContentAreaRootUrl(ev.url);
            this.setIsHomeContentArea(_isContentAreaRootUrl);
            if (!_isContentAreaRootUrl) {
                _getIsContentAreaSubscription.unsubscribe();
            }
        });
        this._subscriptions.push(_getIsContentAreaSubscription);
    }

    private _getIsContentAreaRootUrl(url: string): boolean {
        const urlPathSlugs = this._getUrlPathSlugs(url);
        return urlPathSlugs && urlPathSlugs.length === 1;
    }

    private _setRootAngularUrl(url: string) {
        const rootAngularUrl = url.split('/')[1].split('?')[0].split('#')[0];
        if (this.rootAngularUrl !== rootAngularUrl) {
            this.setRootAngualrUrl(rootAngularUrl);
        }
    }

    public getExternalLink(link: string, queryParams: object): string {
        return this._i18n.getLangBaseUrl() + link + this.extractQueryParams(queryParams);
    }

    /**
     * change the path will be include the place
     * @param name of place
     */
    public addParamToPath(name: string, paramName: string): void {
        let myQueryParams: { [key: string]: string } = {};
        myQueryParams[paramName] = name.replace(' ', '-');

        // Example: navigate to current route with new query params
        const currentRoute = this._router.url.split('?')[0]; // get current path without query
        const routeSegments = currentRoute.startsWith('/') ? currentRoute.substring(1).split('/') : currentRoute.split('/');

        this._router.navigate(routeSegments, { queryParams: myQueryParams });
    }

    public extractQueryParams(params: { [key: string]: any }): string {
        let queryParams = '';
        let separator = '?';
        for (const property in params) {
            if (params[property]) {
                queryParams += `${separator}${property}=${params[property]}`;
                separator = '&';
            }
        }
        return queryParams;
    }
}

export enum SiteLocations {
    Main = 'main',
    Midrash = 'midrash',
    Ask = 'ask',
    Rabbi = 'rabbi',
    Calendar = 'calendar',
    Wiki = 'wiki',
    Dedication = 'dedication',
    Article = 'article',
    Users = 'users',
    Other = 'other',
    GeneralPages = 'generalpages',
    Exams = 'test',
    HofeshLimud = 'hofeshlimud',
    Tags = 'tags',
}

export enum AngularPages {
    MainHomePage = 'main-home-page',
    Home = 'home',
    Category = 'category',
    Filter = 'filter',
    Series = 'series',
    Rabbi = 'rabbi',
    Content = 'content',
    Article = 'article',
    ArticleDetails = 'article-details',
    Halacha = 'halacha',
    Search = 'search',
    GeneralPages = 'generalpages',
    ShabbatTimes = 'shabbattimes',
    TimesTable = 'timestable',
    Moladot = 'moladot',
    BarMitzvaCalc = 'barmitzva',
    PesachTimes = 'pesachtimes',
    Memorial = 'memorial',
    Contact = 'contact',
    Exams = 'test',
    Users = 'users',
    HofeshLimud = 'hofeshlimud',
    ShortsArea = 'shorts-area',
    Tags = 'tags',
}
